import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import type { SnapCategory } from '@/types/database.types';
import SnapCardPreview from '@/components/snap/SnapCardPreview';

interface GenerateCardImageProps {
  id: string;
  title: string;
  description: string;
  categories: SnapCategory[];
  options: { title: string; description: string; imageUrl: string; vote_count?: number }[];
  totalVotes?: number;
  creatorName?: string;
  timeRemaining?: string | number;
  viewCount?: number;
  commentCount?: number;
  likeCount?: number;
  isNew?: boolean;
  isFireSnap?: boolean;
}

export async function generateCardImage(props: GenerateCardImageProps): Promise<Blob> {
  try {
    // Define a unique ID for the component instance we'll render
    const uniqueCardId = `card-preview-${Date.now()}`;

    // Create a temporary container div
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '1200px';
    container.style.height = '630px';
    container.style.backgroundColor = '#fffaf5';
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    container.style.fontFamily = 'Arial, sans-serif';
    document.body.appendChild(container);

    let reactRoot = null;

    try {
      // Render the React component into the container
      reactRoot = createRoot(container);

      // Wait for the component to render
      await new Promise<void>((resolve) => {
        reactRoot.render(
          <SnapCardPreview
            idForCanvas={uniqueCardId}
            totalVotes={props.totalVotes ?? 0}
            {...props}
          />
        );
        // Wait for the next frame to allow React to render
        requestAnimationFrame(() => {
          // Add a small delay to ensure styles are applied
          setTimeout(resolve, 50);
        });
      });

      // Find the rendered card element
      const cardElement = document.getElementById(uniqueCardId);

      if (!cardElement) {
        throw new Error(`Failed to find rendered element with ID: ${uniqueCardId}`);
      }

      // Wait for all images to load
      await Promise.all(
        Array.from(cardElement.querySelectorAll('img'))
          .filter(img => !img.complete)
          .map(img => new Promise(resolve => {
            img.onload = img.onerror = resolve;
          }))
      );

      // Capture the card element with html2canvas
      const canvas = await html2canvas(cardElement, {
        width: 1200,
        height: 630,
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#fffaf5',
        logging: false,
        onclone: (clonedDoc) => {
          clonedDoc.body.style.boxSizing = 'border-box';
        }
      });

      // Convert canvas to Blob
      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas to Blob conversion failed'));
            }
          },
          'image/jpeg',
          0.95 // High quality
        );
      });
    } finally {
      // Cleanup: Unmount the React component and remove the container
      if (reactRoot) {
        reactRoot.unmount();
      }
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }
  } catch (error) {
    console.error('Error generating card image:', error);
    throw new Error(`Failed to generate card image: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}